import { useState, useMemo } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import RuleImportancyRange from "./RuleImportancyRange";
import { useDeleteModelData } from "../../Plan/tableHooks";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { DeleteButton, EditButton } from "../../../common/Buttons";

const Container = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 20px 20px 0px 20px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const TitleArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.div`
  font-weight: 700;
  color: #111;
  margin-bottom: 0;
  font-size: 22px;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  overflow-y: auto;
  padding: 0.5rem 0;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const Card = styled.div`
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #f9f9f9;
  position: relative;
`;

const CardTitle = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardTitleDescription = styled.div`
  font-size: 15px;
  color: #333;
  overflow: hidden;
  font-weight: 400;
  text-overflow: ellipsis;
`;

const CollapsibleSection = styled.div`
  max-height: ${(props) => (props.isExpanded ? "1000px" : "0px")};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

function RuleList({
  modelParamList = [],
  setSliderValues,
  setEditModelId,
  openSnackbar,
  setIsEditMode,
  isEditMode,
  editModelId,
  closeEditModeButtonChip,
  setSchModelSelection,
  setEnteredModelName,
  setEnteredModeDetail,
  group_code,
  project_code,
}) {
  const deleteModelMutation = useDeleteModelData(
    "modelList",
    "/api/rule/deleteRule"
  );
  const [expandedCards, setExpandedCards] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const toggleCardExpansion = (modelId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [modelId]: !prev[modelId],
    }));
  };
  const handleDeleteModel = (selectedModelIds) => {
    if (window.confirm("삭제하시겠습니까? 이 작업은 취소할 수 없습니다.")) {
      const deletePayload = {
        modelIds: selectedModelIds,
        group_code: group_code,
        project_code: project_code,
      };
      deleteModelMutation.mutate(deletePayload, {
        onSuccess: () => {
          openSnackbar("성공적으로 규칙을 삭제하였습니다.");
          setEditModelId(null);
        },
      });
    }
  };
  const onEditStart = (model) => {
    setEditModelId(model.model_id);
    openSnackbar("수정을 시작합니다.");
    setSchModelSelection(
      model.learning_direction === 1
        ? "Forward"
        : model.learning_direction === 2
        ? "Back + Forward"
        : "Backward"
    );
    setIsEditMode(true);
    setSliderValues({
      option1: model.leadtime_level ?? 1,
      option2: model.schedule_adherence ?? 1,
      option3: model.duedate_compliance ?? 1,
      option4: model.order_priority ?? 1,
    });
    setEnteredModelName(model.model_name);
    setEnteredModeDetail(model.model_description);
  };
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  const filteredModels = useMemo(() => {
    if (!searchQuery.trim()) return modelParamList;
    const searchTerm = searchQuery.trim().toLowerCase();
    return modelParamList.filter((model) => {
      const nameMatch = model.model_name?.toLowerCase().includes(searchTerm);
      const descriptionMatch = model.model_description
        ?.toLowerCase()
        .includes(searchTerm);
      return nameMatch || descriptionMatch;
    });
  }, [modelParamList, searchQuery]);
  const displayModels = useMemo(() => {
    if (isEditMode) {
      return modelParamList.sort((a, b) => {
        if (a.model_id === editModelId) return -1;
        if (b.model_id === editModelId) return 1;
        return 0;
      });
    }
    return filteredModels;
  }, [isEditMode, filteredModels, modelParamList, editModelId]);
  return (
    <Container>
      <TitleArea>
        <Title>규칙 리스트</Title>
        <TextField
          placeholder={
            isEditMode
              ? "수정 모드에서는 검색이 비활성화됩니다"
              : "검색어를 입력하세요"
          }
          size="small"
          value={isEditMode ? "" : searchQuery}
          onChange={handleSearchChange}
          disabled={isEditMode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color={isEditMode ? "disabled" : "action"} />
              </InputAdornment>
            ),
          }}
        />
      </TitleArea>
      <CardsContainer>
        {displayModels.length > 0 ? (
          displayModels.map((model) => (
            <Card key={model.model_id}>
              {isEditMode && editModelId === model.model_id && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backdropFilter: "blur(2px)",
                    zIndex: 1,
                  }}
                >
                  <Chip
                    label="수정중"
                    color="success"
                    icon={<ModeEditIcon />}
                    onDelete={closeEditModeButtonChip}
                    sx={{
                      p: 2,
                      fontSize: "0.88rem",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      "& .MuiChip-icon": {
                        color: "white !important",
                        fontSize: "1.2rem",
                      },
                      "& .MuiChip-deleteIcon": {
                        color: "#fa4b4b !important",
                        marginLeft: "1px",
                        fontSize: "1.2rem",
                      },
                    }}
                  />
                </div>
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "space-between",
                  padding: "0 1.5rem",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", gap: "1rem" }}>
                  <CardTitle>{model.model_name}</CardTitle>
                  <CardTitleDescription>
                    {model.model_description}
                  </CardTitleDescription>
                </div>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor:
                        model.learning_direction === 0
                          ? "#f7eded"
                          : model.learning_direction === 1
                          ? "#edf6fc"
                          : "#7dfa87",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      color:
                        model.learning_direction === 0
                          ? "#ff8787"
                          : model.learning_direction === 1
                          ? "#1976d2"
                          : "#127812",
                      height: "100%",
                    }}
                  >
                    {model.learning_direction === 0
                      ? "Backward"
                      : model.learning_direction === 1
                      ? "Forward"
                      : "Back + Forward"}
                  </Typography>
                  <IconButton
                    onClick={() => toggleCardExpansion(model.model_id)}
                    size="small"
                  >
                    {expandedCards[model.model_id] ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>
              <CollapsibleSection isExpanded={expandedCards[model.model_id]}>
                <Box sx={{ padding: "0.5rem 1.5rem" }}>
                  <RuleImportancyRange item={model} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "1rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  <EditButton
                    onClick={() => onEditStart(model)}
                    disabled={isEditMode}
                  />
                  {/* <Button
                      variant="contained"
                      onClick={() => onEditStart(model)}
                      disabled={isEditMode}
                      startIcon={<ModeEditIcon />}
                    >
                      수정
                    </Button> */}
                  <DeleteButton
                    onClick={() => handleDeleteModel(model.model_id)}
                    disabled={isEditMode}
                  />
                  {/* <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteModel(model.model_id)}
                      disabled={isEditMode}
                    >
                      삭제
                    </Button> */}
                </Box>
              </CollapsibleSection>
            </Card>
          ))
        ) : (
          <Box sx={{ textAlign: "center", padding: "20px", color: "#666" }}>
            검색 결과가 없습니다.
          </Box>
        )}
      </CardsContainer>
    </Container>
  );
}

export default RuleList;
